import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { DataSource } from "@firecms/core";
import type { LocalizedString } from "../localization";
import {
  buildEventsPath,
  eventsCollection,
  type Event,
} from "../collections/events";
import {
  buildAccountUsersCollection,
  buildAccountUsersPath,
  type AccountUser,
} from "../collections/accountUsers";
import {
  buildEventUsersCollection,
  buildEventUsersPath,
  type EventUser,
} from "../collections/eventUsers";

export const MANAGER_MEMBERSHIP_ROLE = "manager";
export const PARTICIPANT_MEMBERSHIP_ROLE = "participant";

export type EventMemberRole =
  | "standby"
  | "participant"
  | "operator"
  | "presenter"
  | "manager";

export type AccountUserRole = "manager" | "locked";

export type AccountPublicData = {
  name: string;
  disabled: boolean;
};

/** Mirrors `EventPublic` in firebase/functions (without contentReleaseRef for hub labels). */
export type EventPublic = {
  mainLocale: string;
  name: string;
  title: LocalizedString;
  body: LocalizedString;
  logo: string;
  locked: boolean;
};

export type AccountMembership = {
  role: AccountUserRole;
  accountRefPath: string;
  account: AccountPublicData;
};

export type EventMembershipWire = {
  role: EventMemberRole;
  eventRefPath: string;
};

export type EventMembership = EventMembershipWire & {
  event: EventPublic;
};

type DataSourceFetcher = Pick<DataSource, "fetchEntity">;

export const accountIdFromRef = (accountRefPath: string): string | null => {
  const match = accountRefPath.match(/^accounts\/([^/]+)$/);
  return match?.[1] ?? null;
};

export const eventIdsFromRef = (
  eventRefPath: string
): { accountId: string; eventId: string } | null => {
  const match = eventRefPath.match(/^accounts\/([^/]+)\/events\/([^/]+)$/);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return { accountId: match[1], eventId: match[2] };
};

const emptyEventPublic = (): EventPublic => ({
  mainLocale: "es",
  name: "",
  title: {},
  body: {},
  logo: "",
  locked: false,
});

/** Admin can read `events/{id}`; prefer it over `eventPublic` (may be missing). */
const hydrateEventForAdmin = async (
  dataSource: DataSourceFetcher,
  eventRefPath: string
): Promise<EventPublic> => {
  const ids = eventIdsFromRef(eventRefPath);
  if (!ids) {
    return emptyEventPublic();
  }
  const entity = await dataSource.fetchEntity<Event>({
    path: buildEventsPath({ accountId: ids.accountId }),
    collection: eventsCollection,
    entityId: ids.eventId,
  });
  if (!entity?.values) {
    return emptyEventPublic();
  }
  const data = entity.values;
  return {
    mainLocale: typeof data.mainLocale === "string" ? data.mainLocale : "es",
    name: typeof data.name === "string" ? data.name : "",
    title: (data.title as LocalizedString | undefined) ?? {},
    body: (data.body as LocalizedString | undefined) ?? {},
    logo: typeof data.logo === "string" ? data.logo : "",
    locked: data.locked === true,
  };
};

export async function fetchAccountUserMembership(
  dataSource: DataSourceFetcher,
  userId: string,
  accountId: string
): Promise<boolean> {
  const accountIdObj = { accountId };
  const entity = await dataSource.fetchEntity<AccountUser>({
    path: buildAccountUsersPath(accountIdObj),
    collection: buildAccountUsersCollection(accountIdObj),
    entityId: userId,
  });
  return entity?.values?.role === MANAGER_MEMBERSHIP_ROLE;
}

export async function canManageEvent(
  dataSource: DataSourceFetcher,
  userId: string,
  accountId: string,
  eventId: string
): Promise<boolean> {
  const [accountManager, eventManager] = await Promise.all([
    fetchAccountUserMembership(dataSource, userId, accountId),
    fetchEventUserMembership(dataSource, userId, accountId, eventId),
  ]);
  return accountManager || eventManager;
}

export async function fetchEventUserMembership(
  dataSource: DataSourceFetcher,
  userId: string,
  accountId: string,
  eventId: string
): Promise<boolean> {
  const eventIdObj = { accountId, eventId, spaceId: "" };
  const entity = await dataSource.fetchEntity<EventUser>({
    path: buildEventUsersPath(eventIdObj),
    collection: buildEventUsersCollection(eventIdObj),
    entityId: userId,
  });
  return entity?.values?.role === MANAGER_MEMBERSHIP_ROLE;
}

export async function listMyAccountMemberships(
  _userId?: string
): Promise<AccountMembership[]> {
  const functions = getFunctions(getApp());
  const callable = httpsCallable<undefined, AccountMembership[]>(
    functions,
    "getMyAccountMemberships"
  );
  const result = await callable();
  return result.data ?? [];
}

export async function listMyEventMemberships(
  dataSource: DataSourceFetcher,
  _userId?: string
): Promise<EventMembership[]> {
  const functions = getFunctions(getApp());
  const callable = httpsCallable<undefined, EventMembershipWire[]>(
    functions,
    "getMyEventMemberships"
  );
  const result = await callable();
  const wire = result.data ?? [];
  return Promise.all(
    wire.map(async (membership) => ({
      ...membership,
      event: await hydrateEventForAdmin(dataSource, membership.eventRefPath),
    }))
  );
}

export const managedEventKey = (accountId: string, eventId: string): string =>
  `${accountId}/${eventId}`;

export async function loadManagedEventKeys(
  dataSource: DataSourceFetcher
): Promise<Set<string>> {
  const memberships = await listMyEventMemberships(dataSource);
  const keys = new Set<string>();

  for (const membership of memberships) {
    if (membership.role !== MANAGER_MEMBERSHIP_ROLE) {
      continue;
    }
    const ids = eventIdsFromRef(membership.eventRefPath);
    if (ids) {
      keys.add(managedEventKey(ids.accountId, ids.eventId));
    }
  }

  return keys;
}
